import React, { useEffect, useMemo, useRef } from 'react';
import { decodeOurJWT } from '../decodeOurJWT';
import Cookies from 'js-cookie';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { useGetMeQuery } from 'API/BaseApi';
import { useGetUserType } from '../hooks/useGetUserType';
import { useGetConfigQuery } from 'API/applicationConfig/ApplicationConfigAPI';
import { createAuth0ClientForSpruce } from 'ui-toolkit/authentication/methods/createAuth0ClientForSpruce';
import { getAuth0ClientTypeFromUserType } from 'ui-toolkit/authentication/methods/getAuth0ClientTypeFromUserType';
import { eAccessControlLogoutReason } from 'ui-toolkit/enums/eAccessControlLogoutReason';
import { ClientUrlFactory } from 'ui-toolkit/rest/ClientUrlFactory';
import {
  getLatestActivityTime,
  jwtCookieName,
  spruceClaimsFieldName,
  TIMEOUT_LIMIT,
  TOKEN_EXP_DATETIME,
} from 'types/accessControl/AccessControlConstants';
import { IUseTimer } from './IUseTimer';
import { IAuth0JSONWebToken } from 'types/accessControl/IAuth0JSONWebToken';
import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from 'luxon';
import { eAccessControlUserType } from 'ui-toolkit/enums/eAccessControlUserType';

// Timeout limit defaults to 15 minutes, this means with 5 minute remaining we will renew the token
const RENEW_TOKEN_OFFSET = TIMEOUT_LIMIT / 3;

export const RefreshToken: React.FC<IUseTimer> = ({ timer }) => {
  const auth0ClientRef = useRef<Auth0Client>();
  const { data: configuration } = useGetConfigQuery();
  const userType = useGetUserType();
  const { refetch } = useGetMeQuery();
  const currentCookie = Cookies.get(jwtCookieName);
  const decodedCookie = useMemo(() => (currentCookie ? decodeOurJWT(currentCookie) : null), [currentCookie]);

  useEffect(() => {
    if (!configuration || !userType) {
      return;
    }

    if (!auth0ClientRef.current) {
      const loginConfiguration = configuration?.loginConfigurations[getAuth0ClientTypeFromUserType(userType)];
      createAuth0ClientForSpruce(loginConfiguration, userType).then((auth0Client) => {
        auth0ClientRef.current = auth0Client;
      });
    }
  }, [userType, configuration, auth0ClientRef]);

  return (
    <>
      {auth0ClientRef.current && decodedCookie && (
        <RefreshTokenHandler
          auth0Client={auth0ClientRef.current}
          decodedCookie={decodedCookie}
          timer={timer}
          userType={userType}
          refetch={refetch}
        />
      )}
    </>
  );
};

interface IRefreshTokenHandlerProps extends IUseTimer {
  auth0Client: Auth0Client;
  decodedCookie: IAuth0JSONWebToken;
  userType: eAccessControlUserType;
  refetch: () => Promise<any>;
}

export const RefreshTokenHandler: React.FC<IRefreshTokenHandlerProps> = ({
  auth0Client,
  timer,
  userType,
  refetch,
  decodedCookie,
}) => {
  const [isWorking, setIsWorking] = React.useState(false);

  useEffect(() => {
    // Can't use ONE_SECOND here because Date.now() is in milliseconds already so it has to be converted correctly.
    if (decodedCookie.exp * TOKEN_EXP_DATETIME > Date.now() + RENEW_TOKEN_OFFSET) {
      return;
    }

    if (isWorking) return;
    setIsWorking(true);
    datadogRum.addAction('Starting Refresh Token', {
      currentTime: DateTime.now().toISO(),
      lastActivityTime: getLatestActivityTime().toISO(),
      oldExpiration: new Date(decodedCookie.exp * TOKEN_EXP_DATETIME).toISOString(),
    });
    auth0Client.getTokenSilently({ ignoreCache: true }).then(async (token) => {
      let decodedJwt = decodeOurJWT(token);
      if (!decodedJwt[spruceClaimsFieldName]?.sid) {
        if (userType === eAccessControlUserType.Admin) {
          window.location.href = ClientUrlFactory().urlFor.logoutAuth0(eAccessControlLogoutReason.ADMIN_REDIRECT);
        } else {
          throw new Error(`User Does not have correct JWT claims`);
        }
      } else {
        datadogRum.addAction('Refresh Token Gotten Setting Cookie and Refetching', {
          currentTime: DateTime.now().toISO(),
          lastActivityTime: getLatestActivityTime().toISO(),
          newExpiration: new Date(decodedJwt.exp * TOKEN_EXP_DATETIME).toISOString(),
        });
        Cookies.set(jwtCookieName, token, {
          expires: new Date(decodedJwt.exp * TOKEN_EXP_DATETIME),
          secure: true,
          sameSite: 'strict',
        });
        await refetch();
        setIsWorking(false);
      }
    });
  }, [userType, timer, auth0Client, isWorking, refetch, decodedCookie]);
  return null;
};
